import React, { useState, useCallback } from 'react';
import { AppBar, Toolbar, IconButton, Menu, MenuItem, ListItemIcon, ListItemText } from '@mui/material';
import { Settings, VolumeUp, Language as LanguageIcon, ExitToApp } from '@mui/icons-material';
import { useLogout } from '@/app/hooks/useLogout.ts';
const VadSettingsModal = React.lazy(() =>
  import('@/features/audio/index.ts').then(({ VadSettingsModal }) => ({ default: VadSettingsModal }))
);
import LanguageSettingsDialog from './LanguageSettingsDialog.tsx';
import { useTranslation } from 'react-i18next';
import { I18nNamespace } from '@/i18n/types/i18nNamespace.ts';
import type { I18nCommonNs } from '@/i18n/dictionaries/interfaces.ts';
import { useTheme } from '@mui/material/styles';

const HeiHeader: React.FC = () => {
  const logout = useLogout();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [vadSettingsOpen, setVadSettingsOpen] = useState(false);
  const [languageSettingsOpen, setLanguageSettingsOpen] = useState(false);
  const [translateCommon] = useTranslation(I18nNamespace.Common);
  const theme = useTheme();

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const handleAudioSettings = () => {
    setVadSettingsOpen(true);
    handleMenuClose();
  };
  const handleLanguage = () => {
    setLanguageSettingsOpen(true);
    handleMenuClose();
  };
  const handleLogout = () => {
    logout();
    handleMenuClose();
  };

  return (
    <>
      <AppBar
        position="sticky"
        elevation={0}
        sx={{ backgroundColor: 'transparent', height: theme.mixins.denseToolbar.minHeight }}
      >
        <Toolbar variant="dense" sx={{ height: '100%', position: 'relative' }}>
          {' '}
          {/* Ensure toolbar can be a reference for absolute positioning if needed, or remove if IconButton is relative to AppBar */}
          <IconButton
            aria-label="settings"
            onClick={handleMenuOpen}
            sx={{
              color: 'common.white', // Changed to white
              position: 'absolute',
              top: theme.spacing(0.5), // Adjusted for dense toolbar
              right: theme.spacing(1), // Adjusted for dense toolbar
              zIndex: theme.zIndex.drawer + 1 // Ensure it's above potential sidebars
            }}
          >
            <Settings />
          </IconButton>
        </Toolbar>
      </AppBar>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        transformOrigin={{ horizontal: 'left', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
        PaperProps={{ sx: { mt: 1, width: 250 } }}
      >
        <MenuItem onClick={handleAudioSettings}>
          <ListItemIcon>
            <VolumeUp fontSize="small" />
          </ListItemIcon>
          <ListItemText primary={translateCommon(nameof.full<I18nCommonNs>((n) => n.audioSettings.audioSettings))} />
        </MenuItem>
        <MenuItem onClick={handleLanguage}>
          <ListItemIcon>
            <LanguageIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary={translateCommon(nameof.full<I18nCommonNs>((n) => n.language))} />
        </MenuItem>
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <ExitToApp fontSize="small" />
          </ListItemIcon>
          <ListItemText primary={translateCommon(nameof.full<I18nCommonNs>((n) => n.signOut))} />
        </MenuItem>
      </Menu>
      <VadSettingsModal open={vadSettingsOpen} onClose={() => setVadSettingsOpen(false)} />
      <LanguageSettingsDialog open={languageSettingsOpen} onClose={() => setLanguageSettingsOpen(false)} />
    </>
  );
};
export default HeiHeader;
